import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import {
  getCoachByListingIdApi,
  getUserListingImagesApi,
  getUserWithNamesApi,
} from '../../util/api';
import * as Data from '../../util/data';
import { ensureUser, userDisplayNameAsString } from '../../util/data';
import { normalizeCoachifyImageUrl } from '../../util/coachifyPublicImageUrl';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import CustomBookingPanel from './CustomBookingPanel';
import { LoadingPage } from './ListingPage.shared';
import css from './ListingPage.module.css';

const DEFAULT_COVER_IMAGE =
  'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=1600&q=80';

const coachContextCache = new Map();
const inflightCoachLoads = new Map();

const UnpublishedListingPage = props => {
  const { listingId, currentUser, scrollingDisabled, staticContext, onManageDisableScrolling } =
    props;

  const intl = useIntl();
  const [loadState, setLoadState] = useState('loading');
  const [coachUser, setCoachUser] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const applyCachedContext = cached => {
      if (!isMounted || !cached) {
        return;
      }
      setLoadState(cached.loadState);
      setCoachUser(cached.coachUser || null);
      setCoverImageUrl(cached.coverImageUrl || null);
    };

    const loadCoachContext = async () => {
      try {
        setLoadState('loading');
        const mappingResponse = await getCoachByListingIdApi({ listingId });
        const coachUserId = mappingResponse?.data?.userId;

        if (!coachUserId) {
          const cached = { loadState: 'not-found', coachUser: null, coverImageUrl: null };
          coachContextCache.set(listingId, cached);
          if (isMounted) {
            applyCachedContext(cached);
          }
          return;
        }

        const [userResponse, imagesResponse] = await Promise.all([
          getUserWithNamesApi({ userId: coachUserId }),
          getUserListingImagesApi({ userId: coachUserId }),
        ]);

        if (!isMounted) {
          return;
        }

        const entities = Data.denormalisedResponseEntities(userResponse);
        const user = entities.length > 0 ? entities[0] : null;

        if (!user) {
          const cached = { loadState: 'not-found', coachUser: null, coverImageUrl: null };
          coachContextCache.set(listingId, cached);
          setLoadState('not-found');
          return;
        }

        const bannerPublicData = imagesResponse?.data?.publicData || {};
        const bannerUrl =
          bannerPublicData.coverImageUrl ||
          bannerPublicData.bannerImageUrl ||
          bannerPublicData.profileBannerImageUrl;

        const cached = {
          loadState: 'ready',
          coachUser: user,
          coverImageUrl: bannerUrl ? normalizeCoachifyImageUrl(bannerUrl) : null,
        };
        coachContextCache.set(listingId, cached);
        setCoachUser(user);
        setCoverImageUrl(cached.coverImageUrl);
        setLoadState('ready');
      } catch (error) {
        console.error('Failed to load unpublished listing page context:', error);
        if (isMounted) {
          const status = error?.status || error?.statusCode;
          const cached = {
            loadState: status === 404 ? 'not-found' : 'error',
            coachUser: null,
            coverImageUrl: null,
          };
          coachContextCache.set(listingId, cached);
          applyCachedContext(cached);
        }
      } finally {
        inflightCoachLoads.delete(listingId);
      }
    };

    if (!listingId) {
      setLoadState('not-found');
      return undefined;
    }

    const cached = coachContextCache.get(listingId);
    if (cached) {
      applyCachedContext(cached);
      return undefined;
    }

    if (inflightCoachLoads.has(listingId)) {
      inflightCoachLoads
        .get(listingId)
        .then(() => {
          if (isMounted) {
            applyCachedContext(coachContextCache.get(listingId));
          }
        })
        .catch(() => {});
      return () => {
        isMounted = false;
      };
    }

    const loadPromise = loadCoachContext();
    inflightCoachLoads.set(listingId, loadPromise);

    return () => {
      isMounted = false;
    };
  }, [listingId]);

  const ensuredAuthor = useMemo(() => (coachUser ? ensureUser(coachUser) : null), [coachUser]);
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const pageTitle = authorDisplayName
    ? intl.formatMessage(
        { id: 'ListingPage.unpublishedTitle' },
        { coachName: authorDisplayName, defaultMessage: '{coachName} — Plans & subscriptions' }
      )
    : intl.formatMessage({
        id: 'ListingPage.unpublishedTitleFallback',
        defaultMessage: 'Coach plans & subscriptions',
      });

  const listingStub = useMemo(() => {
    if (!ensuredAuthor) {
      return null;
    }
    return {
      id: { uuid: listingId },
      type: 'listing',
      author: ensuredAuthor,
      attributes: {
        title: authorDisplayName,
        publicData: {},
      },
    };
  }, [authorDisplayName, ensuredAuthor, listingId]);

  const topbar = <TopbarContainer />;

  if (loadState === 'loading') {
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  if (loadState === 'not-found') {
    return <NotFoundPage staticContext={staticContext} />;
  }

  if (loadState === 'error' || !listingStub || !ensuredAuthor) {
    return (
      <Page title={pageTitle} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn topbar={topbar} footer={<FooterContainer />}>
          <div className={css.pageContainer}>
            <p className={css.unpublishedNotice}>
              <FormattedMessage
                id="ListingPage.unpublishedLoadError"
                defaultMessage="Unable to load this page right now. Please try again later."
              />
            </p>
          </div>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const resolvedCoverImage = coverImageUrl || DEFAULT_COVER_IMAGE;

  return (
    <Page title={pageTitle} scrollingDisabled={scrollingDisabled} author={authorDisplayName}>
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.coverImageWrapper}>
          <img src={resolvedCoverImage} alt="" className={css.coverImage} />
          <div className={css.coverOverlay} />
        </div>
        <div className={css.pageContainer}>
          <div className={css.contentWrapperForProductLayout}>
            <div className={css.mainColumnForProductLayout}>
              <p className={css.unpublishedNotice}>
                <FormattedMessage
                  id="ListingPage.unpublishedNotice"
                  defaultMessage="Session booking is not available yet. Subscription plans below are ready to purchase."
                />
              </p>
              <SectionAuthorMaybe
                title={authorDisplayName}
                listing={listingStub}
                authorDisplayName={authorDisplayName}
                currentUser={currentUser}
                onManageDisableScrolling={onManageDisableScrolling}
                sessionCount={0}
                sessionTypes={[]}
                rating={0}
                reviewCount={0}
              />
              <CustomBookingPanel
                className={css.productOrderPanelFullWidth}
                listing={listingStub}
                author={ensuredAuthor}
                currentUser={currentUser}
                listingIdOverride={listingId}
                sessionsUnavailable
              />
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default UnpublishedListingPage;
